import React from 'react';
import {
  Box, Button, IconButton, Paper, Table, TableBody, TableCell, TableHead, TableRow,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PlaceIcon from '@mui/icons-material/Place';
import { IndiaLocationSelect } from '../common/IndiaLocationSelect';
import { brandColors, RADIUS, brandCss } from '../../theme/appTheme';
import { tableHeadRowSx, tableHeadCellSx, tableHeadTextSx, tableBodyCellSx } from '../../theme/tableStyles';

export interface TripStopRow {
  location: string;
  sequence: number;
}

interface TripStopsEditorProps {
  stops: TripStopRow[];
  onChange: (stops: TripStopRow[]) => void;
}

const emptyStop = (sequence: number): TripStopRow => ({ location: '', sequence });

export const TripStopsEditor: React.FC<TripStopsEditorProps> = ({ stops, onChange }) => {
  const rows = stops.length ? stops : [emptyStop(1)];

  const updateRow = (index: number, location: string) => {
    onChange(rows.map((row, i) => (i === index ? { ...row, location } : row)));
  };

  const addRow = () => onChange([...rows, emptyStop(rows.length + 1)]);
  const removeRow = (index: number) => {
    const next = rows.filter((_, i) => i !== index).map((row, i) => ({ ...row, sequence: i + 1 }));
    onChange(next.length ? next : [emptyStop(1)]);
  };

  return (
    <Paper variant="outlined" sx={{ borderRadius: `${RADIUS}px`, overflow: 'hidden', borderColor: brandColors.border }}>
      <Box sx={{ px: 2, py: 1.5, bgcolor: '#f8fafc', borderBottom: `1px solid ${brandColors.border}`, display: 'flex', alignItems: 'center', gap: 1 }}>
        <PlaceIcon sx={{ fontSize: 18, color: brandCss.primary }} />
        <Typography variant="subtitle2" fontWeight={700}>Intermediate stops</Typography>
        <Typography variant="caption" color="text.secondary">Between source and final destination</Typography>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow sx={tableHeadRowSx}>
            <TableCell sx={{ ...tableHeadCellSx(), width: 56 }}><Box component="span" sx={tableHeadTextSx}>#</Box></TableCell>
            <TableCell sx={tableHeadCellSx()}><Box component="span" sx={tableHeadTextSx}>Location</Box></TableCell>
            <TableCell sx={{ ...tableHeadCellSx(), width: 56 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={row.sequence}>
              <TableCell sx={tableBodyCellSx}>{row.sequence}</TableCell>
              <TableCell sx={tableBodyCellSx}>
                <IndiaLocationSelect
                  label=""
                  placeholder="Search India stop location"
                  value={row.location}
                  onChange={(v) => updateRow(index, v)}
                />
              </TableCell>
              <TableCell sx={tableBodyCellSx}>
                <IconButton size="small" onClick={() => removeRow(index)} disabled={rows.length <= 1}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Box sx={{ px: 2, py: 1.5, borderTop: `1px solid ${brandColors.border}` }}>
        <Button size="small" startIcon={<AddIcon />} onClick={addRow} sx={{ textTransform: 'none', fontWeight: 600 }}>
          Add stop
        </Button>
      </Box>
    </Paper>
  );
};
